/// <reference types="vite/client" />

import type { QueryClient } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import * as React from "react";
import type { ISiteScript } from "@newxsystems/types";
import { ClientBootstrap } from "@core/components/ClientBootstrap";
import { SiteScripts } from "@core/site-scripts/render-site-scripts";
import { loadSiteScriptsServer } from "@core/site-scripts/site-scripts.fn";
import appCss from "../styles/app.css?url";
import spaceGroteskLatin from "../../../../templates/daylight/src/styles/fonts/spacegrotesk-latin.woff2?url";
import interTightLatin from "../../../../templates/daylight/src/styles/fonts/intertight-latin.woff2?url";

const Devtools = import.meta.env.DEV
  ? React.lazy(async () => {
      const [{ ReactQueryDevtools }, { TanStackRouterDevtools }] = await Promise.all([
        import("@tanstack/react-query-devtools"),
        import("@tanstack/react-router-devtools"),
      ]);

      return {
        default: function StartDevtools() {
          return (
            <>
              <TanStackRouterDevtools position="bottom-right" />
              <ReactQueryDevtools buttonPosition="bottom-left" />
            </>
          );
        },
      };
    })
  : undefined;

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  loader: () => loadSiteScriptsServer().catch(() => []),
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#001e4f" },
      { title: "Nux" },
      {
        name: "description",
        content: "CMS-rendered Nux site powered by Payload Neon and TanStack Start.",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        as: "font",
        crossOrigin: "anonymous",
        href: spaceGroteskLatin,
        rel: "preload",
        type: "font/woff2",
      },
      {
        as: "font",
        crossOrigin: "anonymous",
        href: interTightLatin,
        rel: "preload",
        type: "font/woff2",
      },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "icon", href: "/favicon-32x32.png", sizes: "32x32", type: "image/png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
  }),
  component: RootComponent,
});

function RootNotFoundComponent() {
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", padding: "2rem" }}>
      <h1 style={{ fontSize: "1.5rem", fontWeight: 600 }}>404 — Page not found</h1>
      <p style={{ color: "#666" }}>The page you are looking for does not exist.</p>
    </div>
  );
}

function RootErrorComponent({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", padding: "2rem" }}>
      <h1 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#c00" }}>Something went wrong</h1>
      <p style={{ fontWeight: 600 }}>{message}</p>
      {import.meta.env.DEV && stack ? (
        <pre
          style={{
            marginTop: "1rem",
            padding: "1rem",
            background: "#f6f6f6",
            border: "1px solid #ddd",
            borderRadius: "6px",
            overflow: "auto",
            fontSize: "0.8rem",
            whiteSpace: "pre-wrap",
          }}
        >
          {stack}
        </pre>
      ) : null}
    </div>
  );
}

function RootComponent() {
  const scripts = Route.useLoaderData();

  return (
    <RootDocument scripts={scripts}>
      <Outlet />
    </RootDocument>
  );
}

function RootDocument({
  children,
  scripts,
}: {
  children: React.ReactNode;
  scripts: ISiteScript[];
}) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <SiteScripts placement="head" scripts={scripts} />
      </head>
      <body>
        <SiteScripts placement="body-start" scripts={scripts} />
        <ClientBootstrap>{children}</ClientBootstrap>
        {Devtools ? (
          <React.Suspense fallback={null}>
            <Devtools />
          </React.Suspense>
        ) : null}
        <SiteScripts placement="body-end" scripts={scripts} />
        <Scripts />
      </body>
    </html>
  );
}
